import { useState } from 'react';
import type { NoteDialogData, NoteRow } from '@/core/models/notes.models';
import { Checkbox } from '@/kit/Checkbox';
import type { DialogComponentProps } from '@/kit/dialog';
import { InputField, TextareaField } from '@/kit/FormField';
import { Radio, RadioGroup } from '@/kit/Radio';
import { RequiredMark } from '@/kit/RequiredMark';
import { Select } from '@/kit/Select';
import { TabGroup } from '@/kit/Tabs';
import { FieldMessage, NoteDialogShell, useNoteSave } from './NoteDialogParts';

export interface ControllingPartyModel {
  parentDetails: string;
  parentCompanyName: string;
  ultimateParentName: string;
  isSmallestLargestGroup: boolean;
  countryOfIncorporation: string | null;
  registeredOffice: string;
  controllingDeclaration: string;
  controllingStatus: 'has' | 'notKnown' | 'notExists' | null;
  controllingPartyName: string;
  ultimate: 'yes' | 'no' | null;
}

function initialModel(data: NoteDialogData | null): ControllingPartyModel {
  const c = data?.note?.content ?? {};
  return {
    parentDetails: c.parentDetails ?? '',
    parentCompanyName: c.parentCompanyName ?? '',
    ultimateParentName: c.ultimateParentName ?? '',
    isSmallestLargestGroup: !!c.isSmallestLargestGroup,
    countryOfIncorporation: c.countryOfIncorporation ?? null,
    registeredOffice: c.registeredOffice ?? '',
    controllingDeclaration: c.controllingDeclaration ?? '',
    controllingStatus: c.controllingStatus ?? null,
    controllingPartyName: c.controllingPartyName ?? '',
    ultimate: c.isUltimateControllingParty === true ? 'yes' : c.isUltimateControllingParty === false ? 'no' : null,
  };
}

/** Controlling party note: the ultimate parent company and the ultimate controlling party, on two tabs. */
export function ControllingPartyDialog({ data, dialogRef }: DialogComponentProps<NoteDialogData, NoteRow>) {
  const [model, setModel] = useState<ControllingPartyModel>(() => initialModel(data));
  const { isSaving, save, submitted } = useNoteSave(data, dialogRef);
  // The API's rules (NoteRules.ControllingParty): a controlling party needs a name, and unless the note says the party
  // is unknown or does not exist, at least one of parent, ultimate parent or controlling party must be named.
  const partyNameMissing = model.controllingStatus === 'has' && model.controllingPartyName.trim().length === 0;
  const noPartyNamed =
    model.parentCompanyName.trim().length === 0 &&
    model.ultimateParentName.trim().length === 0 &&
    model.controllingPartyName.trim().length === 0 &&
    model.controllingStatus !== 'notKnown' &&
    model.controllingStatus !== 'notExists';

  const set = <K extends keyof ControllingPartyModel>(key: K, value: ControllingPartyModel[K]) =>
    setModel((current) => ({ ...current, [key]: value }));
  const text = (key: 'parentCompanyName' | 'ultimateParentName' | 'registeredOffice' | 'controllingPartyName') => ({
    value: model[key],
    onChange: (event: { target: { value: string } }) => set(key, event.target.value),
  });

  const onSave = () =>
    save(() => {
      const { ultimate, ...rest } = model;
      return {
        ...rest,
        isUltimateControllingParty: ultimate === 'yes' ? true : ultimate === 'no' ? false : null,
      };
    }, !partyNameMissing && !noPartyNamed);

  return (
    <NoteDialogShell title="Ultimate Parent Company" isSaving={isSaving} onSave={onSave}>
      <TabGroup
        alignTabs="start"
        tabs={[
          {
            label: 'Ultimate Parent Company',
            children: (
              <div className="mt-1 w-670">
                <div className="form-field textarea">
                  <p className="fw-bold">Please enter details of the Parent and Ultimate Parent Company.</p>
                  <TextareaField
                    fieldClassName="wp-100"
                    autoComplete="off"
                    maxLength={4000}
                    value={model.parentDetails}
                    onChange={(event) => set('parentDetails', event.target.value)}
                  />
                </div>
                <div className="form-field">
                  <p className="fw-bold">Name of Parent Company</p>
                  <InputField
                    fieldClassName="wp-100"
                    autoComplete="off"
                    maxLength={255}
                    invalid={submitted && noPartyNamed}
                    {...text('parentCompanyName')}
                  />
                  <FieldMessage show={submitted && noPartyNamed}>
                    Enter the parent company or the controlling party details.
                  </FieldMessage>
                </div>
                <div className="form-field">
                  <p className="fw-bold">Ultimate Parent Company: Name</p>
                  <InputField fieldClassName="wp-100" autoComplete="off" maxLength={255} {...text('ultimateParentName')} />
                </div>
                <Checkbox
                  checked={model.isSmallestLargestGroup}
                  onChange={(event) => set('isSmallestLargestGroup', event.target.checked)}
                >
                  parent of smallest/largest group that represents group accounts
                </Checkbox>
                <div className="mt-10">
                  <div className="form-field">
                    <p className="fw-bold">Country of Incorporation</p>
                    <Select
                      className="w-200"
                      panelClass="myPanelClass"
                      items={data?.countries ?? []}
                      bindLabel="label"
                      bindValue="label"
                      value={model.countryOfIncorporation}
                      onChange={(value) => set('countryOfIncorporation', value as string | null)}
                    />
                  </div>
                  <div className="form-field">
                    <p className="fw-bold">Registered Office</p>
                    <InputField fieldClassName="wp-100" autoComplete="off" maxLength={500} {...text('registeredOffice')} />
                  </div>
                </div>
              </div>
            ),
          },
          {
            label: 'Ultimate Controlling Party',
            children: (
              <div className="mt-1 w-670">
                <div className="form-field textarea">
                  <p className="fw-bold">
                    Please select the relevant declaration and state whether the controlling party is the ultimate
                    controlling party
                  </p>
                  <TextareaField
                    fieldClassName="wp-100"
                    autoComplete="off"
                    maxLength={4000}
                    value={model.controllingDeclaration}
                    onChange={(event) => set('controllingDeclaration', event.target.value)}
                  />
                </div>
                <RadioGroup
                  name="controllingPartyHas"
                  value={model.controllingStatus}
                  onChange={(value) => set('controllingStatus', value as ControllingPartyModel['controllingStatus'])}
                >
                  <Radio value="has"> Has Controlling Party </Radio>
                </RadioGroup>
                <div className="form-field">
                  <p className="fw-bold">Name {model.controllingStatus === 'has' && <RequiredMark />}</p>
                  <InputField
                    fieldClassName="wp-100"
                    autoComplete="off"
                    charMode="printable"
                    maxLength={255}
                    invalid={submitted && partyNameMissing}
                    {...text('controllingPartyName')}
                  />
                  <FieldMessage show={submitted && partyNameMissing}>
                    Enter the name of the controlling party.
                  </FieldMessage>
                </div>
                <div className="form-field">
                  <p className="fw-bold">Ultimate Controlling Party</p>
                  <RadioGroup
                    name="controllingPartyUltimate"
                    value={model.ultimate}
                    onChange={(value) => set('ultimate', value as ControllingPartyModel['ultimate'])}
                  >
                    <div className="d-flex align-center gap-15">
                      <Radio value="yes"> Yes </Radio>
                      <Radio value="no"> No </Radio>
                    </div>
                  </RadioGroup>
                </div>
                <div className="form-field">
                  <RadioGroup
                    name="controllingPartyStatus"
                    value={model.controllingStatus}
                    onChange={(value) => set('controllingStatus', value as ControllingPartyModel['controllingStatus'])}
                  >
                    <Radio value="notKnown"> Controlling Party not known </Radio>
                    <Radio value="notExists"> Controlling Party does not exist </Radio>
                  </RadioGroup>
                </div>
              </div>
            ),
          },
        ]}
      />
    </NoteDialogShell>
  );
}
